import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  getClassificationRules,
  createClassificationRule,
  updateClassificationRule,
  deleteClassificationRule,
  addKeywordsToRule,
  removeKeywordFromRule,
  getClassificationRuleTargets,
  getClassificationRuleCategories,
  type ClassificationRule,
  type CreateClassificationRuleRequest,
  type UpdateClassificationRuleRequest,
  type AddKeywordsRequest,
  type ClassificationRuleCategoriesResponse,
} from "@/services/api/classification-rules";

const classificationRulesKeys = {
  all: ["classification-rules"] as const,
  lists: () => [...classificationRulesKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) =>
    [...classificationRulesKeys.lists(), { filters }] as const,
};

export function useClassificationRules(
  options?: Omit<
    UseQueryOptions<ClassificationRule[], Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<ClassificationRule[], Error>({
    queryKey: classificationRulesKeys.list(),
    queryFn: getClassificationRules,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCreateClassificationRule() {
  const queryClient = useQueryClient();

  return useMutation<
    ClassificationRule,
    Error,
    CreateClassificationRuleRequest
  >({
    mutationFn: createClassificationRule,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: classificationRulesKeys.lists(),
      });
      toast.success("Classification rule created", {
        description: `Rule for "${data.category}" has been successfully created.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to create classification rule", {
        description: error.message || "An error occurred while creating rule.",
      });
    },
  });
}

export function useUpdateClassificationRule() {
  const queryClient = useQueryClient();

  return useMutation<
    ClassificationRule,
    Error,
    { id: string | number; data: UpdateClassificationRuleRequest }
  >({
    mutationFn: ({ id, data }) => updateClassificationRule(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: classificationRulesKeys.lists(),
      });
      toast.success("Classification rule updated", {
        description: `Rule for "${data.category}" has been successfully updated.`,
      });
    },
    onError: (error) => {
      toast.error("Failed to update classification rule", {
        description: error.message || "An error occurred while updating rule.",
      });
    },
  });
}

export function useDeleteClassificationRule() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string | number>({
    mutationFn: deleteClassificationRule,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: classificationRulesKeys.lists(),
      });
      toast.success("Classification rule deleted", {
        description: "The classification rule has been successfully deleted.",
      });
    },
    onError: (error) => {
      toast.error("Failed to delete classification rule", {
        description: error.message || "An error occurred while deleting rule.",
      });
    },
  });
}

export function useAddKeywordsToRule() {
  const queryClient = useQueryClient();

  return useMutation<
    ClassificationRule,
    Error,
    { id: string | number; data: AddKeywordsRequest }
  >({
    mutationFn: ({ id, data }) => addKeywordsToRule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: classificationRulesKeys.lists(),
      });
      toast.success("Keywords added", {
        description: "Keywords have been successfully added to the rule.",
      });
    },
    onError: (error) => {
      toast.error("Failed to add keywords", {
        description:
          error.message || "An error occurred while adding keywords.",
      });
    },
  });
}

export function useRemoveKeywordFromRule() {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    { id: string | number; keywordId: string | number }
  >({
    mutationFn: ({ id, keywordId }) => removeKeywordFromRule(id, keywordId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: classificationRulesKeys.lists(),
      });
      toast.success("Keyword removed", {
        description: "The keyword has been successfully removed from the rule.",
      });
    },
    onError: (error) => {
      toast.error("Failed to remove keyword", {
        description:
          error.message || "An error occurred while removing keyword.",
      });
    },
  });
}

export function useClassificationRuleTargets(
  options?: Omit<UseQueryOptions<string[], Error>, "queryKey" | "queryFn">
) {
  return useQuery<string[], Error>({
    queryKey: [...classificationRulesKeys.all, "targets"],
    queryFn: getClassificationRuleTargets,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useRuleCategories(
  options?: Omit<
    UseQueryOptions<ClassificationRuleCategoriesResponse, Error>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery<ClassificationRuleCategoriesResponse, Error>({
    queryKey: [...classificationRulesKeys.all, "categories"],
    queryFn: getClassificationRuleCategories,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export { classificationRulesKeys };
